import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

export function InterestModal({ cls, onClose }) {
  const [stage, setStage] = useState("thanks"); // thanks -> submitting -> done
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cls) return;
    // Register the interest event immediately on open.
    api.post("/interest", { class_slug: cls.slug }).catch(() => {});
    setStage("thanks"); setEmail(""); setError("");
  }, [cls]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setStage("submitting"); setError("");
    try {
      await api.post("/interest", { class_slug: cls.slug, email });
      setStage("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Please enter a valid email.");
      setStage("thanks");
    }
  };

  return (
    <AnimatePresence>
      {cls && (
        <motion.div data-testid="interest-modal"
          className="fixed inset-0 z-[150] flex items-center justify-center p-5 bg-ink-900/80 backdrop-blur-md"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            className="relative w-full max-w-lg rounded-3xl glass p-8 sm:p-10 overflow-hidden">
            <div className="absolute inset-0 opacity-40" style={{ background: `radial-gradient(60% 60% at 100% 0%, ${cls.accent}44, transparent 60%)` }} />
            <button data-testid="interest-close" aria-label="Close" onClick={onClose}
              className="absolute top-4 right-4 text-ice/60 hover:text-white transition-colors"><X size={22} /></button>

            <div className="relative">
              <span className="label" style={{ color: cls.accent }}>{cls.name}</span>
              {stage !== "done" ? (
                <>
                  <h3 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3">Thank you for your interest.</h3>
                  <p className="text-ice/70 mt-4 leading-relaxed">
                    If you would like to be notified when {cls.name} launches, leave your email below and we'll let you know as soon as the class is scheduled.
                  </p>
                  <form onSubmit={submitEmail} className="mt-7 flex flex-col sm:flex-row gap-3">
                    <input data-testid="interest-email" type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                      className="flex-1 rounded-full bg-ink-800 border border-white/15 px-5 py-3 text-ice placeholder:text-mist focus:border-cyan/60 outline-none" />
                    <button data-testid="interest-submit" type="submit" disabled={stage === "submitting"}
                      className="book-cta justify-center">
                      {stage === "submitting" ? <Loader2 className="animate-spin" size={16} /> : "Notify me"}
                    </button>
                  </form>
                  {error && <p className="text-red-300 text-sm mt-3" data-testid="interest-error">{error}</p>}
                  <p className="text-mist text-xs mt-4">Or just close this — your interest is already counted.</p>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-5" style={{ background: `${cls.accent}22` }}>
                    <Check size={30} style={{ color: cls.accent }} />
                  </div>
                  <h3 className="font-display text-3xl font-bold text-white">You're on the list.</h3>
                  <p className="text-ice/70 mt-3">We'll email you when {cls.name} launches.</p>
                  <button data-testid="interest-done-close" onClick={onClose} className="book-cta mt-8 mx-auto">Done</button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default InterestModal;
